import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, RefreshCw, UserCheck, UserPlus, Users } from "lucide-react";
import { Link } from "react-router-dom";

type Metrics = {
    total: number;
    returning: number;
    newEntries: number;
    members: number;
    nonMembers: number;
    memberPercentage: number;
    nonMemberPercentage: number;
};

type ReturningUser = {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    member: string;
    createdAt: string | null;
};

type AnalysisResponse = {
    success: boolean;
    message?: string;
    year?: number;
    previousYear?: number;
    years?: number[];
    metrics?: Metrics;
    returningUsers?: ReturningUser[];
};

const API_URL =
    process.env.REACT_APP_ADMIN_API_URL ||
    "https://thenewbrook.com/api/haystack/admin-api.php";
const TOKEN_KEY = "haystack_admin_token";
const emptyMetrics: Metrics = {
    total: 0,
    returning: 0,
    newEntries: 0,
    members: 0,
    nonMembers: 0,
    memberPercentage: 0,
    nonMemberPercentage: 0,
};

const AdminAnalysis = () => {
    const token = sessionStorage.getItem(TOKEN_KEY) || "";
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [previousYear, setPreviousYear] = useState(selectedYear - 1);
    const [availableYears, setAvailableYears] = useState<number[]>([]);
    const [metrics, setMetrics] = useState<Metrics>(emptyMetrics);
    const [returningUsers, setReturningUsers] = useState<ReturningUser[]>([]);
    const [isLoading, setIsLoading] = useState(Boolean(token));
    const [error, setError] = useState("");

    useEffect(() => {
        window.scrollTo(0, 0);
        if (!token) return;

        const controller = new AbortController();
        const loadAnalysis = async () => {
            setIsLoading(true);
            setError("");
            try {
                const url = new URL(API_URL);
                url.searchParams.set("action", "analysis");
                url.searchParams.set("year", String(selectedYear));
                const response = await fetch(url.toString(), {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                });
                const data: AnalysisResponse = await response.json();
                if (response.status === 401) sessionStorage.removeItem(TOKEN_KEY);
                if (!response.ok || !data.success) {
                    throw new Error(data.message || "Could not load registration analysis.");
                }
                setMetrics(data.metrics || emptyMetrics);
                setReturningUsers(data.returningUsers || []);
                setAvailableYears(data.years || []);
                setPreviousYear(data.previousYear ?? selectedYear - 1);
            } catch (err) {
                if (err instanceof DOMException && err.name === "AbortError") return;
                setError(err instanceof Error ? err.message : "Could not load registration analysis.");
            } finally {
                if (!controller.signal.aborted) setIsLoading(false);
            }
        };
        loadAnalysis();
        return () => controller.abort();
    }, [selectedYear, token]);

    const yearOptions = useMemo(
        () => Array.from(new Set([new Date().getFullYear(), ...availableYears])).sort((a, b) => b - a),
        [availableYears]
    );

    if (!token) {
        return (
            <main className="flex min-h-screen items-center justify-center bg-[#f5f2ec] px-6 text-center">
                <div className="max-w-md rounded-2xl bg-white p-8 shadow-xl">
                    <h1 className="font-clashSemibold text-2xl">Admin sign-in required</h1>
                    <p className="mt-3 font-inter text-sm leading-6 text-black/60">
                        Sign in from the registrations page before viewing analysis.
                    </p>
                    <Link to="/admin" className="mt-6 inline-block rounded-lg bg-[#613A00] px-5 py-3 font-inter text-sm font-semibold text-white">
                        Go to admin sign in
                    </Link>
                </div>
            </main>
        );
    }

    const cards = [
        { label: `${selectedYear} registrations`, value: metrics.total, icon: Users },
        { label: `Returned from ${previousYear}`, value: metrics.returning, icon: UserCheck },
        { label: `New since ${previousYear}`, value: metrics.newEntries, icon: UserPlus },
        { label: "Members", value: `${metrics.memberPercentage}%`, detail: `${metrics.members} people`, icon: Users },
        { label: "Non-members", value: `${metrics.nonMemberPercentage}%`, detail: `${metrics.nonMembers} people`, icon: Users },
    ];

    return (
        <main className="min-h-screen bg-[#f5f2ec] px-4 py-6 text-[#131313] sm:px-8 lg:px-12 lg:py-10">
            <div className="mx-auto max-w-[1400px]">
                <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <Link to="/admin" className="inline-flex items-center gap-2 font-inter text-sm font-semibold text-[#613A00] hover:underline">
                            <ArrowLeft size={17} /> Back to registrations
                        </Link>
                        <p className="mt-6 font-clashSemibold text-sm uppercase tracking-[0.25em] text-[#613A00]">
                            Haystack Conference
                        </p>
                        <h1 className="mt-2 font-clashSemibold text-3xl sm:text-4xl">
                            {selectedYear} Registration Analysis
                        </h1>
                    </div>
                    <label className="font-inter text-sm font-semibold">
                        Analysis year
                        <select
                            value={selectedYear}
                            onChange={(event) => setSelectedYear(Number(event.target.value))}
                            className="ml-3 rounded-lg border border-black/15 bg-white px-4 py-2.5 font-normal outline-none focus:border-[#613A00]"
                        >
                            {yearOptions.map((year) => <option key={year} value={year}>{year}</option>)}
                        </select>
                    </label>
                </header>

                {error && (
                    <p role="alert" className="mt-6 rounded-lg bg-red-50 px-4 py-3 font-inter text-sm text-red-700">
                        {error}
                    </p>
                )}

                {isLoading ? (
                    <div className="mt-12 flex items-center justify-center gap-3 py-20 font-inter text-black/50">
                        <RefreshCw className="animate-spin" size={20} /> Loading analysis…
                    </div>
                ) : (
                    <>
                        <section className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
                            {cards.map(({ label, value, detail, icon: Icon }) => (
                                <article key={label} className="rounded-xl border border-black/10 bg-white p-5 shadow-sm">
                                    <div className="flex items-center gap-2 text-black/50">
                                        <Icon size={18} />
                                        <p className="font-inter text-sm">{label}</p>
                                    </div>
                                    <p className="mt-4 font-clashSemibold text-3xl">{value}</p>
                                    {detail && <p className="mt-1 font-inter text-xs text-black/45">{detail}</p>}
                                </article>
                            ))}
                        </section>

                        <section className="mt-5 rounded-xl border border-black/10 bg-white p-5 shadow-sm sm:p-7">
                            <h2 className="font-clashSemibold text-xl">Membership split</h2>
                            <div className="mt-5 flex h-4 overflow-hidden rounded-full bg-black/10" aria-label={`${metrics.memberPercentage}% members and ${metrics.nonMemberPercentage}% non-members`}>
                                <div className="bg-[#613A00]" style={{ width: `${metrics.memberPercentage}%` }} />
                                <div className="bg-[#EFC66F]" style={{ width: `${metrics.nonMemberPercentage}%` }} />
                            </div>
                            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 font-inter text-sm text-black/60">
                                <span><i className="mr-2 inline-block h-2.5 w-2.5 rounded-full bg-[#613A00]" />Members: {metrics.memberPercentage}%</span>
                                <span><i className="mr-2 inline-block h-2.5 w-2.5 rounded-full bg-[#EFC66F]" />Non-members: {metrics.nonMemberPercentage}%</span>
                            </div>
                        </section>

                        <section className="mt-5 overflow-hidden rounded-xl border border-black/10 bg-white shadow-sm">
                            <div className="border-b border-black/10 p-5 sm:p-7">
                                <h2 className="font-clashSemibold text-xl">Returning registrations</h2>
                                <p className="mt-2 font-inter text-sm text-black/55">
                                    People registered in both {previousYear} and {selectedYear}. Email is used when provided; otherwise, the normalized phone number is used.
                                </p>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[850px] font-inter text-sm">
                                    <thead className="bg-[#f7f5f1] text-left text-xs uppercase tracking-wider text-black/55">
                                        <tr>
                                            <th className="px-6 py-4 font-semibold">Name</th>
                                            <th className="px-6 py-4 font-semibold">Email</th>
                                            <th className="px-6 py-4 font-semibold">Phone</th>
                                            <th className="px-6 py-4 font-semibold">Member</th>
                                            <th className="px-6 py-4 font-semibold">Registered</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-black/10">
                                        {returningUsers.length ? returningUsers.map((user) => (
                                            <tr key={user.id}>
                                                <td className="px-6 py-4 font-semibold">{user.firstName} {user.lastName}</td>
                                                <td className="px-6 py-4 text-black/65">{user.email || "—"}</td>
                                                <td className="px-6 py-4 text-black/65">{user.phone}</td>
                                                <td className="px-6 py-4 capitalize text-black/65">{user.member || "—"}</td>
                                                <td className="px-6 py-4 text-black/65">{user.createdAt || String(selectedYear)}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan={5} className="px-6 py-12 text-center text-black/50">
                                                    No registrations from {previousYear} returned in {selectedYear}.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </>
                )}
            </div>
        </main>
    );
};

export default AdminAnalysis;
